import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api, { IOrdinalMarketSummary, IOrdinalOrder } from 'api/api'
import { useOrdinalSettlementStatus } from 'hooks/useOrdinalSettlementStatus'
import { useOrdinalMarketRunStatus } from 'hooks/useOrdinalMarketRunStatus'
import { isOrdinalStateConnected, subscribeOrdinalStateConnection } from 'state/ordinalRealtime'
import {
    currentUserCanTradeOrdinals,
    currentUserHasOrdinalsMarket,
} from 'components/panels/ordinalMarketFeature'
import { MarketConfirmTicket, MarketEntryTray } from 'components/market/MarketOrderModals'
import cn from 'classnames'
import styles from 'components/answers/FileEmbed/ordinal-market-controls.module.scss'
import { ordinalMarketQueryOptions } from './ordinalMarketQueryOptions'

const MIN_PRICE_SATS = 1000
export function formatSatsCompact(sats: number | null | undefined): string {
    const n = Number(sats || 0)
    if (n >= 1_000_000) {
        const m = n / 1_000_000
        return `${m >= 10 ? Math.round(m) : Math.round(m * 10) / 10}M`
    }
    if (n >= 1_000) {
        const k = n / 1_000
        return `${k >= 100 ? Math.round(k) : Math.round(k * 10) / 10}k`
    }
    return String(n)
}

type PopoverKind = 'none' | 'list' | 'offer' | 'offers' | 'buy' | 'menu'

/**
 * The bid/ask rail rendered over ordinal artwork wherever an ordinal renders
 * (ordinals stream tiles, ordinal wrapper answers, ...). Fully self-contained:
 * checks market availability, loads market state for its ordinal, and manages
 * the off-chain order book and atomic sale
 * settlement. The two sides mirror each other: List and Offer create the
 * standing intents, Buy and Sell commit against the other side's.
 */
export default function OrdinalMarketControls({
    ordinalId,
    ownerId,
    origin,
    initialMarket,
}: {
    ordinalId?: string | null
    ownerId?: string | null
    /** Origin outpoint — lets the modals resolve artwork via the descriptor. */
    origin?: string | null
    /** Seed from a stream payload so tiles don't each fetch market state. */
    initialMarket?: IOrdinalMarketSummary | null
}) {
    const currentUserId = window.gon?.currentUser?.id
    const enabled = !!ordinalId && !!currentUserId && currentUserHasOrdinalsMarket()
    const canTrade = enabled && currentUserCanTradeOrdinals()
    const queryClient = useQueryClient()
    const [popover, setPopover] = useState<PopoverKind>('none')
    const [error, setError] = useState<string | null>(null)
    // Set once a signed offer's on-chain withdrawal has started.
    const [notice, setNotice] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    const [settlingOrderId, setSettlingOrderId] = useState<string | null>(null)
    // A local-signing order waits for this device's signatures before it opens.
    const [authorizingSide, setAuthorizingSide] = useState<'ask' | 'bid' | null>(null)
    const [offers, setOffers] = useState<IOrdinalOrder[] | null>(null)
    const [confirmOffer, setConfirmOffer] = useState<IOrdinalOrder | null>(null)
    const [socketConnected, setSocketConnected] = useState(isOrdinalStateConnected)

    useEffect(() => subscribeOrdinalStateConnection(setSocketConnected), [])

    // Settlement takes real time — build, broadcast, record. Mint and send have
    // always narrated that; this makes the buyer's rail say what is happening
    // instead of sitting on a frozen "Sale pending…".
    const { settlementMessage, trackSettlement } = useOrdinalSettlementStatus()

    const marketQueryKey = useMemo(
        () => ['ordinalMarket', ordinalId, currentUserId || null],
        [currentUserId, ordinalId],
    )
    const { data: market } = useQuery({
        queryKey: marketQueryKey,
        queryFn: async () => {
            return api.getOrdinalMarketStateFor(String(ordinalId))
        },
        ...ordinalMarketQueryOptions(initialMarket),
        // Settlement outcomes arrive as ordinal state events, so the 3s poll is
        // gone. The slow fallback only runs while the socket is down.
        refetchInterval: query => {
            const pending = query.state.data?.settlement_pending || settlingOrderId
            if (!pending) return false
            return socketConnected ? false : 15_000
        },
        enabled,
    })

    useEffect(() => {
        if (!initialMarket) return
        // A stream item can be older than the rail after a remount; the shared
        // revision says which one it is.
        const current = queryClient.getQueryData<IOrdinalMarketSummary>(marketQueryKey)
        const currentRevision = Number(current?.state_revision ?? 0)
        const seedRevision = Number(initialMarket.state_revision ?? 0)
        if (current && Number.isFinite(currentRevision) && seedRevision < currentRevision) return
        queryClient.setQueryData(marketQueryKey, initialMarket)
    }, [initialMarket, marketQueryKey, queryClient])

    const isOwner =
        market?.viewer?.is_owner ?? (!!ownerId && !!currentUserId && ownerId === currentUserId)
    const ask = market?.ask || null
    const viewerBid = market?.viewer?.bid || null
    const authorizingOffer = authorizingSide === 'bid'
    const authorizingListing = authorizingSide === 'ask'
    const bidCount = market?.bid_count || 0
    const settlementPending = !!market?.settlement_pending || !!settlingOrderId

    useEffect(() => {
        // The sale event already patched every mounted tile in place. Clearing
        // the local flag is all that is left — invalidating the walls here is
        // exactly what used to make the purchased tile vanish mid-purchase.
        if (settlingOrderId && market?.settlement_pending === false) setSettlingOrderId(null)
    }, [market?.settlement_pending, settlingOrderId])

    const setMarket = useCallback(
        (next: IOrdinalMarketSummary | null | undefined) => {
            if (next) queryClient.setQueryData(marketQueryKey, next)
            else queryClient.invalidateQueries({ queryKey: marketQueryKey })
        },
        [queryClient, marketQueryKey],
    )

    // Listing, offering, and settling all change this ordinal's rail and nothing
    // else about the wall. The server publishes the same state to every other
    // viewer, so there is no reason to refetch a ranked stream here.
    const setMarketAfterMutation = setMarket

    // Signing usually finishes within seconds; the opened order then arrives as a
    // state event. Re-read this rail whichever way the run ends, so a missed
    // frame cannot leave it on the old shape, and toast a failure.
    const finishAuthorization = useCallback(() => {
        setAuthorizingSide(null)
        setMarket(null)
    }, [setMarket])
    const { message: authorizationMessage, trackRun: trackAuthorization } =
        useOrdinalMarketRunStatus({
            workflowClass: 'AppTemporal::Workflows::OrdinalMarketAuthorizationWorkflow',
            eventBusTopic: 'ordinal_market_status',
            failureMessage: 'This device could not sign the order.',
            onFinished: finishAuthorization,
        })

    const closePopover = useCallback(() => {
        setPopover('none')
        setError(null)
        setNotice(null)
        setOffers(null)
        setConfirmOffer(null)
    }, [])

    const submitOrder = useCallback(
        async (side: 'ask' | 'bid', priceSats: number) => {
            if (!Number.isSafeInteger(priceSats) || priceSats < MIN_PRICE_SATS) {
                setError(`Enter at least ${MIN_PRICE_SATS.toLocaleString()} sats.`)
                return
            }
            setBusy(true)
            setError(null)
            try {
                const res = await api.createOrdinalOrder(String(ordinalId), {
                    side,
                    price_sats: priceSats,
                })
                setMarketAfterMutation(res?.market)
                if (res?.status === 'authorizing' && trackAuthorization(res)) {
                    setAuthorizingSide(side)
                }
                closePopover()
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Something went wrong.')
            } finally {
                setBusy(false)
            }
        },
        [ordinalId, setMarketAfterMutation, closePopover, trackAuthorization],
    )

    const cancelOrder = useCallback(
        async (orderId: string) => {
            setBusy(true)
            setError(null)
            try {
                const res = await api.cancelOrdinalOrder(String(ordinalId), orderId)
                if (res?.withdrawal) {
                    // The order closes once the withdrawal claims it; re-read then.
                    setNotice(
                        'Withdrawing: your funds go back to your wallet on-chain. Track it in Manage Ordinals.',
                    )
                    setMarket(null)
                    return false
                }
                setMarketAfterMutation(res?.market)
                return true
            } catch (err) {
                console.error('Failed to cancel ordinal order:', err)
                setError(err instanceof Error ? err.message : 'Could not cancel the order.')
                // Unknown rail state after a failed cancel: re-read this one
                // ordinal rather than every wall showing it.
                setMarket(null)
                return false
            } finally {
                setBusy(false)
            }
        },
        [ordinalId, setMarket, setMarketAfterMutation],
    )

    const openOffers = useCallback(() => {
        setPopover('offers')
        setError(null)
    }, [])

    useEffect(() => {
        if (popover !== 'offers') return
        // A replacement or withdrawal can arrive while the owner is reading
        // the list or confirming a sale. Reload only this open order book.
        setOffers(null)
        setConfirmOffer(null)
        if (!isOwner || settlementPending) {
            closePopover()
            return
        }
        let cancelled = false
        void api
            .getOrdinalOrders(String(ordinalId))
            .then(res => {
                if (cancelled) return
                const latest = queryClient.getQueryData<IOrdinalMarketSummary>(marketQueryKey)
                if (Number(res?.market?.state_revision ?? 0) < Number(latest?.state_revision ?? 0))
                    return
                setOffers((res?.orders || []).filter(order => order.side === 'bid'))
                setMarket(res?.market)
            })
            .catch(err => {
                if (!cancelled)
                    setError(err instanceof Error ? err.message : 'Could not load offers.')
            })
        return () => {
            cancelled = true
        }
    }, [
        ordinalId,
        popover,
        market?.state_revision,
        isOwner,
        settlementPending,
        closePopover,
        queryClient,
        marketQueryKey,
        setMarket,
    ])

    const declineOffer = useCallback(
        async (orderId: string) => {
            setBusy(true)
            try {
                const res = await api.declineOrdinalOrder(String(ordinalId), orderId)
                setMarketAfterMutation(res?.market)
                setOffers(prev => (prev || []).filter(order => order.id !== orderId))
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Could not decline the offer.')
            } finally {
                setBusy(false)
            }
        },
        [ordinalId, setMarketAfterMutation],
    )

    const settleOrder = useCallback(
        async (order: IOrdinalOrder) => {
            setBusy(true)
            setError(null)
            try {
                const res = await api.settleOrdinalOrder(String(ordinalId), order.id, order)
                setSettlingOrderId(order.id)
                trackSettlement(res)
                setMarketAfterMutation(res?.market)
                closePopover()
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Could not complete the sale.')
            } finally {
                setBusy(false)
            }
        },
        [ordinalId, setMarketAfterMutation, closePopover, trackSettlement],
    )

    if (!enabled) return null

    const stop = (ev: React.SyntheticEvent) => {
        ev.stopPropagation()
    }

    return (
        <div
            className={styles.rail}
            data-testid="ordinal-market-controls"
            data-ordinal-market-rail
            onClick={stop}
            onMouseDown={stop}
            onPointerDown={stop}
            onTouchStart={stop}
        >
            {/* Non-owners read the list price from Buy. During settlement, keep
                the frozen listing visible while all actions are disabled. */}
            {ask && settlementPending && (
                <span className={styles.pill}>Listed {formatSatsCompact(ask.price_sats)}</span>
            )}
            {bidCount > 0 && isOwner && !settlementPending ? (
                <button
                    type="button"
                    className={cn(styles.pillButton, styles.bidPill)}
                    aria-haspopup="dialog"
                    aria-expanded={popover === 'offers'}
                    disabled={busy}
                    onClick={() => {
                        if (popover === 'offers') closePopover()
                        else void openOffers()
                    }}
                >
                    {bidCount} {bidCount === 1 ? 'offer' : 'offers'}
                    {market?.top_bid_sats ? ` · top ${formatSatsCompact(market.top_bid_sats)}` : ''}
                    <span className={styles.chevron} aria-hidden="true">
                        ▾
                    </span>
                </button>
            ) : bidCount > 0 && market?.top_bid_sats ? (
                <span
                    className={cn(styles.pill, styles.bidPill)}
                    title={`Top of ${bidCount} standing ${bidCount === 1 ? 'offer' : 'offers'}`}
                >
                    Top offer {formatSatsCompact(market.top_bid_sats)} · {bidCount}{' '}
                    {bidCount === 1 ? 'offer' : 'offers'}
                </span>
            ) : null}

            {!canTrade && !settlementPending && !isOwner && ask && (
                <span className={styles.pill}>Listed {formatSatsCompact(ask.price_sats)}</span>
            )}
            {settlementPending ? (
                <button type="button" className={styles.action} disabled>
                    {settlementMessage || 'Sale pending…'}
                </button>
            ) : isOwner ? (
                authorizingListing ? (
                    <button
                        type="button"
                        className={styles.action}
                        disabled
                        title={authorizationMessage || undefined}
                    >
                        Signing listing…
                    </button>
                ) : ask ? (
                    <button
                        type="button"
                        className={styles.pillButton}
                        aria-haspopup="menu"
                        aria-expanded={popover === 'menu'}
                        disabled={busy}
                        onClick={() => {
                            setError(null)
                            setPopover(popover === 'menu' ? 'none' : 'menu')
                        }}
                    >
                        Listed {formatSatsCompact(ask.price_sats)}
                        <span className={styles.chevron} aria-hidden="true">
                            ▾
                        </span>
                    </button>
                ) : canTrade ? (
                    <button
                        type="button"
                        className={styles.action}
                        onClick={() => {
                            setPopover('list')
                            setError(null)
                        }}
                    >
                        List
                    </button>
                ) : null
            ) : (
                <>
                    {canTrade && ask && (
                        <button
                            type="button"
                            className={styles.action}
                            disabled={busy || settlingOrderId === ask.order_id}
                            onClick={() => {
                                setPopover('buy')
                                setError(null)
                            }}
                        >
                            {settlingOrderId === ask.order_id
                                ? 'Buying…'
                                : `Buy ${formatSatsCompact(ask.price_sats)}`}
                        </button>
                    )}
                    {authorizingOffer ? (
                        <button
                            type="button"
                            className={styles.action}
                            disabled
                            title={authorizationMessage || undefined}
                        >
                            Signing offer…
                        </button>
                    ) : viewerBid ? (
                        <button
                            type="button"
                            className={styles.pillButton}
                            aria-haspopup="menu"
                            aria-expanded={popover === 'menu'}
                            disabled={busy}
                            onClick={() => {
                                setError(null)
                                setPopover(popover === 'menu' ? 'none' : 'menu')
                            }}
                        >
                            Offered {formatSatsCompact(viewerBid.price_sats)}
                            <span className={styles.chevron} aria-hidden="true">
                                ▾
                            </span>
                        </button>
                    ) : canTrade ? (
                        <button
                            type="button"
                            className={styles.action}
                            onClick={() => {
                                setPopover('offer')
                                setError(null)
                            }}
                        >
                            Offer
                        </button>
                    ) : null}
                </>
            )}

            {popover === 'menu' && isOwner && ask && (
                <div
                    className={styles.popover}
                    role="menu"
                    aria-label="Listing actions"
                    onClick={stop}
                >
                    {canTrade && (
                        <button
                            type="button"
                            role="menuitem"
                            className={styles.menuItem}
                            disabled={busy}
                            onClick={() => {
                                setError(null)
                                setPopover('list')
                            }}
                        >
                            Change price
                        </button>
                    )}
                    <button
                        type="button"
                        role="menuitem"
                        className={cn(styles.menuItem, styles.menuItemDanger)}
                        disabled={busy}
                        onClick={async () => {
                            if (await cancelOrder(ask.order_id)) closePopover()
                        }}
                    >
                        Cancel listing
                    </button>
                    {error && (
                        <div className={styles.popoverError} role="alert">
                            {error}
                        </div>
                    )}
                </div>
            )}

            {/* Outlives the bid: the withdrawal closes the order before its spend confirms. */}
            {popover === 'menu' && !isOwner && !viewerBid && notice && (
                <div className={styles.popover} role="status" onClick={stop}>
                    <div className={styles.popoverNotice}>{notice}</div>
                </div>
            )}

            {popover === 'menu' && !isOwner && viewerBid && (
                <div
                    className={styles.popover}
                    role="menu"
                    aria-label="Offer actions"
                    onClick={stop}
                >
                    {canTrade && (
                        <button
                            type="button"
                            role="menuitem"
                            className={styles.menuItem}
                            disabled={busy}
                            onClick={() => {
                                setError(null)
                                setPopover('offer')
                            }}
                        >
                            Change offer
                        </button>
                    )}
                    <button
                        type="button"
                        role="menuitem"
                        className={cn(styles.menuItem, styles.menuItemDanger)}
                        disabled={busy}
                        onClick={async () => {
                            if (await cancelOrder(viewerBid.id)) closePopover()
                        }}
                    >
                        Withdraw
                    </button>
                    {error && (
                        <div className={styles.popoverError} role="alert">
                            {error}
                        </div>
                    )}
                    {notice && (
                        <div className={styles.popoverNotice} role="status">
                            {notice}
                        </div>
                    )}
                </div>
            )}

            {canTrade && (popover === 'list' || popover === 'offer') && (
                <MarketEntryTray
                    mode={popover}
                    origin={origin}
                    market={market}
                    initialPriceSats={popover === 'list' ? ask?.price_sats : viewerBid?.price_sats}
                    busy={busy}
                    error={error}
                    onSubmit={priceSats =>
                        submitOrder(popover === 'list' ? 'ask' : 'bid', priceSats)
                    }
                    onClearError={() => setError(null)}
                    onClose={closePopover}
                />
            )}

            {canTrade && popover === 'buy' && ask && (
                <MarketConfirmTicket
                    kind="buy"
                    origin={origin}
                    priceSats={Number(ask.price_sats)}
                    busy={busy}
                    error={error}
                    onConfirm={() =>
                        settleOrder({
                            id: ask.order_id,
                            ordinal_id: String(ordinalId),
                            side: 'ask',
                            price_sats: ask.price_sats,
                            status: 'open',
                            user_id: ask.seller_id,
                        })
                    }
                    onClose={closePopover}
                />
            )}

            {popover === 'offers' && (
                <div
                    className={styles.popover}
                    role="dialog"
                    aria-label="Ordinal offers"
                    data-testid="ordinal-offers"
                    onClick={stop}
                >
                    <div className={styles.popoverTitle}>Offers</div>
                    {offers === null && !error && (
                        <div className={styles.popoverHint}>Loading…</div>
                    )}
                    {error && <div className={styles.popoverError}>{error}</div>}
                    {(offers || []).map(offer => (
                        <div
                            key={offer.id}
                            className={styles.offerRow}
                            data-testid="ordinal-offer-row"
                            data-order-id={offer.id}
                        >
                            <div className={styles.offerMeta}>
                                <span className={styles.offerAmount}>
                                    {Number(offer.price_sats).toLocaleString()} sats
                                </span>
                                {offer.user_name && (
                                    <span className={styles.offerBidder}>@{offer.user_name}</span>
                                )}
                            </div>
                            <button
                                type="button"
                                className={styles.popoverButton}
                                disabled={busy}
                                onClick={() => declineOffer(offer.id)}
                            >
                                Decline
                            </button>
                            {canTrade && (
                                <button
                                    type="button"
                                    className={styles.popoverPrimary}
                                    disabled={busy}
                                    onClick={() => {
                                        setError(null)
                                        setConfirmOffer(offer)
                                    }}
                                >
                                    {settlingOrderId === offer.id ? 'Selling…' : 'Sell'}
                                </button>
                            )}
                        </div>
                    ))}
                    {offers !== null && offers.length === 0 && (
                        <div className={styles.popoverHint}>No open offers.</div>
                    )}
                    <div className={styles.popoverActions}>
                        <button
                            type="button"
                            className={styles.popoverButton}
                            onClick={closePopover}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {canTrade && confirmOffer && (
                <MarketConfirmTicket
                    kind="accept"
                    origin={origin}
                    priceSats={Number(confirmOffer.price_sats)}
                    listPriceSats={ask ? Number(ask.price_sats) : null}
                    bidderName={confirmOffer.user_name || null}
                    busy={busy}
                    error={error}
                    onConfirm={() => settleOrder(confirmOffer)}
                    onClose={() => setConfirmOffer(null)}
                />
            )}
        </div>
    )
}
